import { useMemo, useState } from 'react';

import { addMonths, format, startOfMonth } from 'date-fns';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { displayNumber } from 'utils/misc';

import { queries } from 'RestAPIs/rapini';

import { FormControlLabel, Switch } from '@mui/material';
import Box from '@mui/material/Box';

import { DataGrid } from 'components/shared';
import DateInterval from 'components/shared/DateInterval';

type Props = {
  employeeNumber: number;
};

// datum, dienstverband, kilometers, bedrag, status
const TravelDistancesOverview = ({ employeeNumber }: Props) => {
  const { t } = useTranslation();

  const columnProperties = [
    {
      field: 'start',
      flex: 1,
      headerName: t(`date`)
    },
    {
      field: 'travelDistance',
      flex: 1,
      headerName: t(`travelDistance`),
      type: 'number'
    },
    {
      field: 'travelDistanceCompensation',
      flex: 1,
      headerName: t(`travelDistanceCompensation`),
      type: 'number'
    },
    {
      field: 'status',
      flex: 1,
      headerName: t(`status`)
    }
  ];

  const [currentInterval, setCurrentInterval] = useState({
    startDate: startOfMonth(new Date()),
    endDate: dayjs(addMonths(startOfMonth(new Date()), 1))
      .add(-1, 'day')
      .format('YYYY-MM-DD')
  });
  const { data: duties, isLoading } = queries.useGetDuties(
    dayjs(currentInterval.startDate).format('YYYY-MM-DD'),
    dayjs(currentInterval.endDate).format('YYYY-MM-DD'),
    //@ts-expect-error TODO: BE should accept array of ids
    [employeeNumber].join(','),
    true,
    false,
    false,
    true,
    false,
    true,
    false
  );

  const [checked, setChecked] = useState<boolean>(false);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const rows = useMemo(() => {
    // Sort duties by start date
    const sortedDuties = duties
      ? duties
          .filter(
            (a) => !dayjs(a.start).isBefore(dayjs(currentInterval.startDate))
          )
          .sort((a, b) => {
            return new Date(a.start).getTime() - new Date(b.start).getTime();
          })
      : [];

    const filteredDuties = sortedDuties.filter(function (duty) {
      // if checked return all duties, else return only duties with travelDistanceCompensation > 0
      return checked || (duty?.travelDistanceCompensation ?? 0) > 0;
    });

    return filteredDuties.map((duty) => ({
      id: duty.id,
      start: format(new Date(duty.start), 'dd-MM-yyyy'),
      status: t(`dutyStatus.${duty.status}`),
      travelDistance: displayNumber(duty.travelDistance ?? 0, 1),
      travelDistanceCompensation:
        '€' + displayNumber(duty.travelDistanceCompensation ?? 0, 2)
    }));
  }, [duties, checked]);

  const onIntervalChanged = (interval) => {
    setCurrentInterval({
      startDate: interval.startDate,
      endDate: interval.endDate
    });
  };

  const totalKilometers = useMemo(() => {
    let total = 0;
    duties?.forEach((duty) => {
      if (duty.status !== 'REJECTED') {
        total += duty?.travelDistance ?? 0;
      }
    });
    return total;
  }, [duties]);

  const totalKilometerCompensation = useMemo(() => {
    let total = 0;
    duties?.forEach((duty) => {
      if (duty.status !== 'REJECTED') {
        total += duty?.travelDistanceCompensation ?? 0;
      }
    });
    return total;
  }, [duties]);

  return (
    <>
      <DateInterval onSelected={onIntervalChanged} view="month" />
      <FormControlLabel
        label={t(`showDutiesWithoutKilometers`)}
        control={<Switch checked={checked} onChange={handleChange} />}
      />
      <DataGrid loading={isLoading} columns={columnProperties} rows={rows} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <h4>
          {t(`totalKilometers`)}: {displayNumber(totalKilometers, 1)}
        </h4>
      </Box>
      <Box>
        <h4>
          {t(`totalKilometersCompensation`)}:{' '}
          {'€' + displayNumber(totalKilometerCompensation, 2)}
        </h4>
      </Box>
    </>
  );
};

export default TravelDistancesOverview;
